import React, { useState, useEffect } from 'react';
import { useOfficerTitles } from "../hooks/useOfficerTitles";
import { useChapters } from "../hooks/useChapters";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Users, Shield, Award, RefreshCw, Save, ArrowLeft, UserCog } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import PageLayout from "../components/PageLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


// Title lists come from useOfficerTitles (DB-driven)

export default function PromotionPage() {
  const { active: CHAPTERS } = useChapters();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { all_titles: TITLES, promotion_eligible_titles: ALLOWED_TITLES } = useOfficerTitles();
  
  const [members, setMembers] = useState([]);
  const [discordRoles, setDiscordRoles] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [currentDiscordRoles, setCurrentDiscordRoles] = useState([]);
  const [nickname, setNickname] = useState('');
  const [chapter, setChapter] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(false);
  const [updatingNickname, setUpdatingNickname] = useState(false);
  const [updatingMemberInfo, setUpdatingMemberInfo] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  // Check access and fetch data on mount
  useEffect(() => {
    const checkAccessAndFetch = async () => {
      try {
        // Get user info
        const authResponse = await axios.get(`${BACKEND_URL}/api/auth/verify`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        
        const userChapter = authResponse.data?.chapter;
        const userTitle = authResponse.data?.title;
        
        // Check if user is National with allowed title
        if (userChapter === 'National' && ALLOWED_TITLES.includes(userTitle)) {
          setHasAccess(true);
          
          // Fetch members and roles
          const [membersRes, rolesRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/members`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${BACKEND_URL}/api/discord/roles`, { headers: { Authorization: `Bearer ${token}` } })
          ]);
          
          setMembers(membersRes.data || []);
          setDiscordRoles(rolesRes.data?.roles || []);
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error('Error:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    
    checkAccessAndFetch();
  }, [token, navigate]);

  // When member is selected, fetch their current Discord roles
  const handleMemberSelect = async (memberId) => {
    setSelectedMemberId(memberId);
    const member = members.find(m => m.id === memberId);
    setSelectedMember(member);
    setSelectedRoles([]);
    setCurrentDiscordRoles([]);
    setNickname(member?.handle || '');
    setChapter(member?.chapter || '');
    setTitle(member?.title || '');
    
    if (memberId) {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/discord/member/${memberId}/roles`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.roles) {
          setCurrentDiscordRoles(response.data.roles);
          setSelectedRoles(response.data.roles.map(r => r.id));
        }
        if (response.data?.nickname) {
          setNickname(response.data.nickname);
        }
      } catch (error) {
        console.error('Error fetching member Discord info:', error);
      }
    }
  };

  // Update Discord roles
  const handleUpdateRoles = async () => {
    if (!selectedMemberId) {
      toast.error('Please select a member');
      return;
    }
    
    setPromoting(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/discord/member/${selectedMemberId}/roles`,
        { role_ids: selectedRoles },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.success) {
        toast.success(response.data.message || 'Roles updated successfully');
        // Refresh member's roles
        handleMemberSelect(selectedMemberId);
      } else {
        toast.error(response.data?.message || 'Failed to update roles');
      }
    } catch (error) {
      console.error('Error updating roles:', error);
      toast.error(error.response?.data?.detail || 'Failed to update Discord roles');
    } finally {
      setPromoting(false);
    }
  };

  // Update Discord nickname
  const handleUpdateNickname = async () => {
    if (!selectedMemberId) {
      toast.error('Please select a member');
      return;
    }
    
    if (!nickname.trim()) {
      toast.error('Please enter a nickname');
      return;
    }
    
    setUpdatingNickname(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/discord/member/${selectedMemberId}/nickname`,
        { nickname: nickname.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.success) {
        toast.success(response.data.message || 'Nickname updated successfully');
      } else {
        toast.error(response.data?.message || 'Failed to update nickname');
      }
    } catch (error) {
      console.error('Error updating nickname:', error);
      toast.error(error.response?.data?.detail || 'Failed to update Discord nickname');
    } finally {
      setUpdatingNickname(false);
    }
  };

  // Update member chapter and title in database
  const handleUpdateMemberInfo = async () => {
    if (!selectedMemberId) {
      toast.error('Please select a member');
      return;
    }
    
    if (!chapter || !title) {
      toast.error('Please select both chapter and title');
      return;
    }
    
    setUpdatingMemberInfo(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/members/${selectedMemberId}`,
        { chapter, title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data) {
        toast.success(`Member updated to ${chapter} ${title}`);
        setSelectedMember(prev => ({ ...prev, chapter, title }));
        const membersRes = await axios.get(`${BACKEND_URL}/api/members`, { headers: { Authorization: `Bearer ${token}` } });
        setMembers(membersRes.data || []);
      }
    } catch (error) {
      console.error('Error updating member info:', error);
      toast.error(error.response?.data?.detail || 'Failed to update member info');
    } finally {
      setUpdatingMemberInfo(false);
    }
  };

  // Toggle role selection
  const toggleRole = (roleId) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  // Sort roles by position (higher position = more important)
  const sortedRoles = [...discordRoles].sort((a, b) => b.position - a.position);

  // Loading state
  if (loading) {
    return (
      <PageLayout title="Member Promotion">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </PageLayout>
    );
  }

  // Access denied
  if (!hasAccess) {
    return (
      <PageLayout title="Member Promotion">
        <div className="space-y-6">
          <Button 
            variant="outline" 
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Card className="bg-red-900/30 border-red-600/50">
            <CardContent className="pt-6">
              <p className="text-red-300">Access denied. This page is only available to National officers (Prez, VP, S@A, ENF, T, SEC).</p>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Member Promotion">
      <div className="space-y-6">
        {/* Back Button */}
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Member Selection */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              Select Member
            </CardTitle>
            <CardDescription>
              Choose a member to manage their Discord roles and nickname
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedMemberId} onValueChange={handleMemberSelect}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="member-select">
                <SelectValue placeholder="Select a member..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600 max-h-[300px]">
                {members.map(member => (
                  <SelectItem 
                    key={member.id} 
                    value={member.id}
                    className="text-white hover:bg-slate-600"
                  >
                    {member.handle} - {member.chapter} {member.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedMember && (
              <div className="mt-4 p-4 bg-slate-700/50 rounded-lg">
                <p className="text-white font-medium">{selectedMember.handle}</p>
                <p className="text-slate-400 text-sm">{selectedMember.chapter} - {selectedMember.title}</p>
                {selectedMember.name && (
                  <p className="text-slate-400 text-sm">{selectedMember.name}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Discord Nickname */}
        {selectedMemberId && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Award className="w-5 h-5" />
                Discord Nickname
              </CardTitle>
              <CardDescription>
                Update the member's display name on Discord
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Input
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="Enter nickname..."
                  className="bg-slate-700 border-slate-600 text-white flex-1"
                  data-testid="nickname-input"
                />
                <Button 
                  onClick={handleUpdateNickname}
                  disabled={updatingNickname}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="update-nickname-btn"
                >
                  {updatingNickname ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    'Update Nickname'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chapter & Title Management */}
        {selectedMemberId && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <UserCog className="w-5 h-5" />
                Chapter & Title
              </CardTitle>
              <CardDescription>
                Update the member's chapter and title in the system database
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Chapter</Label>
                  <Select value={chapter} onValueChange={setChapter}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="chapter-select">
                      <SelectValue placeholder="Select chapter..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {CHAPTERS.map(ch => (
                        <SelectItem key={ch} value={ch} className="text-white hover:bg-slate-600">
                          {ch}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Title</Label>
                  <Select value={title} onValueChange={setTitle}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="title-select">
                      <SelectValue placeholder="Select title..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {TITLES.map(t => (
                        <SelectItem key={t} value={t} className="text-white hover:bg-slate-600">
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {(chapter !== selectedMember?.chapter || title !== selectedMember?.title) && (
                <div className="mt-4 p-3 bg-amber-900/30 border border-amber-600/50 rounded-lg">
                  <p className="text-amber-300 text-sm">
                    Changing from <span className="font-medium">{selectedMember?.chapter} {selectedMember?.title}</span> to <span className="font-medium">{chapter} {title}</span>
                  </p>
                </div>
              )}
              
              <div className="mt-4 flex justify-end">
                <Button 
                  onClick={handleUpdateMemberInfo}
                  disabled={updatingMemberInfo || (chapter === selectedMember?.chapter && title === selectedMember?.title)}
                  className="bg-amber-600 hover:bg-amber-700 disabled:bg-slate-600"
                  data-testid="update-member-info-btn"
                >
                  {updatingMemberInfo ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {updatingMemberInfo ? 'Updating...' : 'Update Chapter & Title'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Discord Roles */}
        {selectedMemberId && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Discord Roles
              </CardTitle>
              <CardDescription>
                Select the roles this member should have on Discord
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Current Roles */}
              {currentDiscordRoles.length > 0 && (
                <div className="mb-4">
                  <Label className="text-slate-300 text-sm mb-2 block">Current Roles:</Label>
                  <div className="flex flex-wrap gap-2">
                    {currentDiscordRoles.map(role => (
                      <Badge 
                        key={role.id}
                        style={{ 
                          backgroundColor: role.color ? `${role.color}33` : '#374151',
                          borderColor: role.color || '#4B5563',
                          color: role.color || '#9CA3AF'
                        }}
                        className="border"
                      >
                        {role.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Role Selection */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
                {sortedRoles.map(role => (
                  <div 
                    key={role.id}
                    className="flex items-center space-x-2 p-2 rounded bg-slate-700/50 hover:bg-slate-700"
                  >
                    <Checkbox
                      id={`role-${role.id}`}
                      checked={selectedRoles.includes(role.id)}
                      onCheckedChange={() => toggleRole(role.id)}
                      className="border-slate-500"
                    />
                    <label 
                      htmlFor={`role-${role.id}`}
                      className="text-sm cursor-pointer flex items-center gap-2"
                      style={{ color: role.color || '#9CA3AF' }}
                    >
                      <span 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: role.color || '#4B5563' }}
                      />
                      {role.name}
                    </label>
                  </div>
                ))}
              </div>

              {/* Update Button */}
              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={handleUpdateRoles}
                  disabled={promoting}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="update-roles-btn"
                >
                  {promoting ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {promoting ? 'Updating...' : 'Update Discord Roles'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
